import React from 'react'
import { View } from '@components/atoms'

import { defaultCountries, parseCountry } from 'react-international-phone'

import {
	useAppDispatch,
	useAppSelector,
	paymentActions,
	PaymentStateType,
	PaymentCustomerInfoType,
} from '@redux/index'
import { CustomerType } from '@schemas'
import { produce } from 'immer'

import { ChevronDown } from './ChevronDown'

/**
 * 결제 화면의 Customer 블록 — Monolith(2안). 구 구현은 `systems/payment/PaymentCustomerInfo.tsx`.
 *
 * redux(payment.paymentCustomerInfo) 흐름·검증 메시지 키는 그대로다. 바꾼 것은 입력 껍데기다 —
 * MUI TextField 와 국가 코드용 MUI Select + 국기 이미지를 걷어내고(DESIGN_SYSTEM §5 MUI 외형 금지),
 * 밑줄 입력과 네이티브 셀렉트로 옮겼다. 국가 목록은 그대로 react-international-phone 의
 * `defaultCountries` 이고, redux 에 저장하는 값도 그대로 국가번호(dialCode) 문자열이다.
 *
 * 화면에 보이는 기본값(미국)과 저장값이 어긋나지 않도록, 아직 값이 없을 때는 마운트에서
 * 기본 국가의 번호를 한 번 넣는다 — 구 화면은 국기만 미국으로 보이고 값은 비어 있어
 * 손대지 않으면 검증에서 걸렸다.
 */
export const MonolithPaymentCustomerInfo = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const reduxCustomer: CustomerType = useAppSelector(state => state.customerAuth.customer)
	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const paymentCustomer: PaymentCustomerInfoType = useAppSelector(
		state => state.payment.paymentCustomerInfo,
	)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	const countryList = React.useMemo(
		() =>
			defaultCountries
				.map(aCountry => parseCountry(aCountry))
				.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0)),
		[],
	)

	// state — 같은 국가번호를 쓰는 나라가 여럿이라(+1) 화면 선택은 iso2 로 따로 들고 있는다.
	const [phoneIso2, setPhoneIso2] = React.useState<string>(() => {
		const matched = countryList.find(
			aCountry => aCountry.dialCode === paymentCustomer.phoneCountryCode,
		)
		return matched?.iso2 ?? 'us'
	})

	const update = (patch: Partial<PaymentCustomerInfoType>) => {
		const newCustomer = produce(paymentCustomer, draft => {
			Object.assign(draft, patch)
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	React.useEffect(() => {
		if (isLogedin && reduxCustomer.id !== '' && paymentCustomer.email === '') {
			update({ email: reduxCustomer.email })
		}
	}, [isLogedin, reduxCustomer, paymentCustomer])

	React.useEffect(() => {
		if (paymentCustomer.phoneCountryCode === '') {
			const initial = countryList.find(aCountry => aCountry.iso2 === phoneIso2)

			if (initial !== undefined) {
				update({ phoneCountryCode: initial.dialCode })
			}
		}
	}, [paymentCustomer.phoneCountryCode])

	const onPhoneCountryChange = (iso2: string) => {
		const thisCountry = countryList.find(aCountry => aCountry.iso2 === iso2)

		setPhoneIso2(iso2)

		if (thisCountry !== undefined) {
			update({ phoneCountryCode: thisCountry.dialCode })
		}
	}

	return (
		<View className="mnPaySection">
			<View className="mnPaySectionHead">
				<span className="mnKicker">Customer</span>
				<p className="mnPaySectionNote">
					This is where delivery messages go. A wrong address or number means they do not
					arrive.
				</p>
			</View>

			<View className="mnPaySectionBody">
				{paymentMethod === 'credit' && (
					<>
						<View className="mnFormRow2">
							<View className="mnField">
								<label className="mnLabel" htmlFor="mnPayGivenName">
									Given name
								</label>
								<input
									id="mnPayGivenName"
									type="text"
									autoComplete="given-name"
									className={`mnInput ${
										errorMessages.customer.givenName !== '' ? 'error' : ''
									}`}
									value={paymentCustomer.givenName}
									onChange={e => update({ givenName: e.target.value })}
								/>
								{errorMessages.customer.givenName !== '' && (
									<span className="mnError">{errorMessages.customer.givenName}</span>
								)}
							</View>

							<View className="mnField">
								<label className="mnLabel" htmlFor="mnPayFamilyName">
									Family name
								</label>
								<input
									id="mnPayFamilyName"
									type="text"
									autoComplete="family-name"
									className={`mnInput ${
										errorMessages.customer.familyName !== '' ? 'error' : ''
									}`}
									value={paymentCustomer.familyName}
									onChange={e => update({ familyName: e.target.value })}
								/>
								{errorMessages.customer.familyName !== '' && (
									<span className="mnError">{errorMessages.customer.familyName}</span>
								)}
							</View>
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayEmail">
								Email
							</label>
							<input
								id="mnPayEmail"
								type="email"
								autoComplete="email"
								className={`mnInput ${
									errorMessages.customer.email !== '' ? 'error' : ''
								}`}
								value={paymentCustomer.email}
								onChange={e => update({ email: e.target.value })}
							/>
							{errorMessages.customer.email !== '' && (
								<span className="mnError">{errorMessages.customer.email}</span>
							)}
						</View>
					</>
				)}

				<View className="mnFormRow2">
					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPayPhoneCountry">
							Country code
						</label>
						<View className="mnPaySelectWrap">
							<select
								id="mnPayPhoneCountry"
								className={`mnInput mnPaySelect ${
									errorMessages.customer.phoneCountryCode !== '' ? 'error' : ''
								}`}
								value={phoneIso2}
								onChange={e => onPhoneCountryChange(e.target.value)}
							>
								{countryList.map(aCountry => (
									<option key={aCountry.iso2} value={aCountry.iso2}>
										{`${aCountry.name} +${aCountry.dialCode}`}
									</option>
								))}
							</select>
							<ChevronDown />
						</View>
						{errorMessages.customer.phoneCountryCode !== '' && (
							<span className="mnError">{errorMessages.customer.phoneCountryCode}</span>
						)}
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPayPhoneNumber">
							Phone number
						</label>
						<input
							id="mnPayPhoneNumber"
							type="tel"
							autoComplete="tel-national"
							className={`mnInput ${
								errorMessages.customer.phoneNumber !== '' ? 'error' : ''
							}`}
							value={paymentCustomer.phoneNumber}
							onChange={e => update({ phoneNumber: e.target.value })}
						/>
						{errorMessages.customer.phoneNumber !== '' && (
							<span className="mnError">{errorMessages.customer.phoneNumber}</span>
						)}
					</View>
				</View>
			</View>
		</View>
	)
}
